//no normal store. just an interface to access the project db
import { Project } from '@/classes/entities/Project'
import type { TSchedule } from '@/pages/single-user/projects/projects-page/components/projects-list/Schedule'
import { getSchedule } from '@/pages/single-user/projects/projects-page/components/projects-list/Schedule'
import { DateTime } from 'luxon'
import { defineStore, acceptHMRUpdate } from 'pinia'
import { Brackets } from 'typeorm'
import StoreManager from '@/classes/manager/StoreManager'

type TState = {
  paginatedProjects: Array<Project>
  activeProject: Project | null
  mainProject: Project | null
  count: number
  page: number
  pages: number
  searchString: string | null
  schedule: TSchedule | null
}

const initialState: TState = {
  paginatedProjects: [],
  activeProject: null,
  mainProject: null,
  count: 0,
  page: 1,
  pages: 1,
  searchString: null,
  schedule: null,
}

const perPage = 60

const useProjectStore = defineStore('projectStore', {
  state: () => {
    return { ...initialState }
  },
  getters: {
    project: (state) => state.activeProject,
    getNavigationParams: (state) => {
      const { mainProject: prj } = state
      if (
        !prj?.destination_city ||
        !prj.destination_zip ||
        !prj.destination_street
      )
        return ''
      return `${prj.destination_street} ${prj.destination_zip} ${prj.destination_city}`
    },
    isOnFirstProjectPage: (state) => {
      return state.page === 1
    },
    isOnLastProjectPage: (state) => {
      return state.page === state.pages
    },
    hasMoreThanOneProjectPage: (state) => {
      return state.count > perPage
    },
  },

  actions: {
    async sync() {
      await Project.sync()
      this.$state.count = await Project.count()
      const today = DateTime.local().startOf('day')
      this.$state.schedule = getSchedule(
        today,
        today.plus({ days: 14 }),
        await Project.find(),
      )
      void StoreManager.persist(this)
    },
    async setActiveProjectById(id: number) {
      this.$state.page = 1
      this.$state.searchString = null
      const project = await Project.findOneBy({ id })
      if (!project) {
        throw new Error(`ProjectStore: Project with id ${id} not found`)
      }
      this.$state.activeProject = project
      if (!project.parent_id) {
        this.$state.mainProject = project
      } else {
        const parent = (
          await Project.find({ where: { id: project.parent_id } })
        )[0]
        this.$state.activeProject.parent = parent
        this.$state.mainProject = parent
      }
      void this.loadSubsOfMainAndActive()
    },

    nextPage() {
      this.$state.page++
      void this.loadProjects()
    },

    prevPage() {
      if (this.$state.page === 1) return
      this.$state.page--
      void this.loadProjects()
    },

    async getProjectById(id: number) {
      const yesterday = DateTime.local().minus({ days: 1 }).toISO()
      const project = await Project.findOneBy({ id })
      if (!project) return null
      if (project.deleted_at && project.deleted_at >= yesterday) return null
      if (project.parent_id && !(await this.getProjectById(project.parent_id)))
        return null
      return project
    },

    async getProjectByQr(token: string) {
      const yesterday = DateTime.local().minus({ days: 1 }).toISO()
      const project = await Project.findOneBy({ qr_token: token })
      if (!project) return null
      if (project.deleted_at && project.deleted_at >= yesterday) return null
      if (project.parent_id && !(await this.getProjectById(project.parent_id)))
        return null
      return project
    },

    async forPage(page: number) {
      this.$state.page = page
      await this.loadProjects()
    },

    async search(query: string) {
      this.$state.page = 1
      this.$state.searchString = query
      await this.loadProjects()
    },

    async loadProjects() {
      const startIndex = (this.$state.page - 1) * perPage
      const today = DateTime.local().startOf('day')
      const completedProjects = await Project.createQueryBuilder('project')
        .where('project.completed_date is not null')
        .andWhere('project.completed_date < :before', {
          before: today.minus({ days: 2 }).toISO(),
        })
        .getMany()
      const completedProjectIds = completedProjects.map((prj) => prj.id)
      const query = Project.createQueryBuilder('project')
        .leftJoinAndSelect('project.project_dates', 'ProjectDate')
        .leftJoinAndSelect('project.contacts', 'Contact')
        .andWhere('deleted_at is null')
        .andWhere(
          new Brackets((qb) => {
            qb.where('project.completed_date >= :before', {
              before: today.minus({ days: 2 }).toISO(),
            }).orWhere('project.completed_date is null')
          }),
        )

      if (this.$state.searchString) {
        const searchString = this.$state.searchString
        query.andWhere(
          new Brackets((qb) => {
            qb.where('project.name like :name', { name: `%${searchString}%` })
              .orWhere('project.number like :number', {
                number: `%${searchString}%`,
              })
              .orWhere('project.customer like :customer', {
                customer: `%${searchString}%`,
              })
              .orWhere('project.destination_name like :destination_name', {
                destination_name: `%${searchString}%`,
              })
              .orWhere('project.destination_city like :destination_city', {
                destination_city: `%${searchString}%`,
              })
          }),
        )
      }

      this.$state.pages = Math.ceil((await query.getCount()) / perPage)
      let paginatedProjects = await query
        .orderBy('project.parent_id', 'ASC')
        .addOrderBy('project.name', 'ASC')
        .take(perPage)
        .skip(startIndex)
        .getMany()

      // yes this should be done in the query, but it does not work
      paginatedProjects = paginatedProjects.filter((prj) => {
        if (!prj.parent_id) return true
        return !completedProjectIds.includes(prj.parent_id)
      })
      // fetch and associate parent projects
      const parentIds = paginatedProjects
        .filter((prj) => prj.parent_id)
        .map((prj) => prj.parent_id)

      const parentProjects = await Project.findByIds([...new Set(parentIds)])
      const parentMap = new Map(
        parentProjects.map((parent) => [parent.id, parent]),
      )
      paginatedProjects.forEach((prj) => {
        if (prj.parent_id) {
          prj.parent = parentMap.get(prj.parent_id) ?? null
        }
      })

      const mainProjects = paginatedProjects.filter((prj) => !prj.parent_id)
      const subProjects = paginatedProjects.filter((prj) => prj.parent_id)
      const sortedPage = [...mainProjects, ...subProjects]

      this.$state.paginatedProjects = sortedPage
    },

    async subForPage(page: number) {
      this.$state.page = page
      await this.loadSubsOfMainAndActive()
    },

    async subSearch(query: string) {
      this.$state.page = 1
      this.$state.searchString = query
      await this.loadSubsOfMainAndActive()
    },

    async _loadSubProjects(prj?: Project) {
      if (!prj) return []
      const query = Project.createQueryBuilder('project').where(
        'project.parent_id is :id',
        { id: prj.id },
      )
      if (this.$state.searchString) {
        const searchString = this.$state.searchString
        query.andWhere(
          new Brackets((qb) => {
            qb.where('project.name like :name', { name: `%${searchString}%` })
              .orWhere('project.number like :number', {
                number: `%${searchString}%`,
              })
              .orWhere('project.customer like :customer', {
                customer: `%${searchString}%`,
              })
              .orWhere('project.destination_name like :destination_name', {
                destination_name: `%${searchString}%`,
              })
              .orWhere('project.destination_city like :destination_city', {
                destination_city: `%${searchString}%`,
              })
          }),
        )
      }
      const startIndex = (this.$state.page - 1) * perPage
      this.$state.pages = Math.ceil((await query.getCount()) / perPage)
      const paginatedProjects = await query
        .take(perPage)
        .skip(startIndex)
        .orderBy('project.name', 'ASC')
        .getMany()

      return paginatedProjects
    },

    async refreshSchedule() {
      const today = DateTime.local().startOf('day')
      this.$state.schedule = getSchedule(
        today,
        today.plus({ days: 14 }),
        await Project.find(),
      )
    },

    async _loadSubProjectsOfActive() {
      if (!this.$state.activeProject) return
      this.$state.activeProject.children = await this._loadSubProjects(
        this.$state.activeProject,
      )
    },

    async _loadSubProjectsOfMain() {
      if (!this.$state.mainProject) return
      this.$state.mainProject.children = await this._loadSubProjects(
        this.$state.mainProject,
      )
    },

    async loadSubsOfMainAndActive() {
      await Promise.all([
        this._loadSubProjectsOfActive(),
        this._loadSubProjectsOfMain(),
      ])
    },
    hydrate(state: string) {
      this.$state = JSON.parse(state)
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useProjectStore, import.meta.hot))
}

export { useProjectStore }
